import React, {
    Dispatch,
    createContext,
    useCallback,
    useContext,
    useMemo,
} from 'react'
import orderService, { toVoidOrderRequest } from 'Services/api/order.service'
import Decimal from 'decimal.js'
import {
    VoidCompReasonModel,
    CustomerLookupContract,
    SaleSlimModel,
    DiscountContract,
    DiscountLevel,
    DiscountType,
    OrderContract,
    OrderDiscountModel,
    OrderDiscountType,
    OrderLineStatus,
    OrderStatus,
    OrderLookupContract,
    UnavailableItemsContract,
    OrderDetailContract,
} from 'models'
import { useDispatch } from 'react-redux'
import {
    OrderItemViewModel,
    ISelectItemInCart,
    IPickupSettingForm,
    IDeliverySettingForm,
    DiscountContractWithCustom,
    OrderProcessingMode,
    OrderFulfilmentType,
    SellState,
    MenuViewModel,
    ErrorOrderItemViewType,
} from 'store/models/sellModels'
import {
    autoDiscountByItemsMapping,
    getItemLevelAutomaticDiscounts,
} from 'views/Sale/discount/discountMapper/discountMapper'
import { cloneDeep, isEqual, keyBy, maxBy, sortBy } from 'lodash-es'
import {
    processOfflineOrder,
    voidOfflineOrder,
    OfflineOrderContract,
    getOfflineOrder,
} from 'Services/api/offline/order.service.offline'
import orderFulfillmentService from 'Services/api/orderFulfillment.service'
import { AxiosResponse } from 'axios'
import {
    getItemName,
    changeBaseItemIDToVariantID,
    isOrderHasPayment,
    toQuickServiceOrderLineContract,
    isSameOrderItem,
    isUseSamePriceList,
} from 'components/Sell/utils/common'
import { OrderChannel } from 'constant/OrderChannels'
import { showErrorMessage } from 'store/actions/ui.action'
import { getCommonError, CommonError } from 'utilities/commonError'
import errorMessages from 'utilities/errorMessages'
import pendingOrderDbService from 'utilities/offlineDb/pendingOrderDbService'
import { SIMPLE_DISCOUNT_TYPE } from 'views/Discounts/hooks/useUpsertDiscountSchema'
import {
    ISaleDependencies,
    useSaleDependenciesContext,
} from 'views/Sale/contexts/SaleDependenciesContext'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import { useFormat } from 'Services/localize/format'
import useOrderQuery from 'views/Sale/hooks/useOrderQuery'
import { useQuickSaleSelectReducer } from 'store/reducers/sell.reducer'
import usePermission from 'hooks/usePermission/usePermission'
import { ISellActionType } from 'store/reducers/ISellActionType'
import { usePosContext } from 'views/Sale/contexts/PosProvider'
import RecalculateOrder from '../hooks/RecalculateOrder'
import inventoryService from 'Services/api/inventory.service'
import OrderItemViewError from 'store/utils/OrderItemViewError'
import Guid from 'utilities/Guid'
import { useTranslation } from 'react-i18next'
import toast from 'utilities/toast/toast'
import useBuildOrderItems from 'components/Sell/hook/useBuildOrderItems'
import { OrderLineNotificationContract } from 'hooks/useOrderNotification/OrderLineNotificationContract'
import { OrderNotificationContract } from 'hooks/useOrderNotification/OrderNotificationContract'
import { validateAvailability } from 'utilities/availability/availabilityHelper'
import { ProcessSaleContract } from 'models/ProcessSaleContract'

type IQuickServiceSaleContext = SellState & {
    dispatch: Dispatch<ISellActionType>
}

const QuickServiceSaleContext = createContext<IQuickServiceSaleContext>(
    {} as IQuickServiceSaleContext,
)
export const QuickServiceSaleContextProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const { state, dispatch } = useQuickSaleSelectReducer()

    return (
        <QuickServiceSaleContext.Provider value={{ ...state, dispatch }}>
            {children}
        </QuickServiceSaleContext.Provider>
    )
}

const mapEntireDiscountToOrderDiscount = (
    discount: DiscountContractWithCustom,
): OrderDiscountModel => ({
    orderDiscountID: discount.orderLineDiscountId,
    discountType: discount.isCustomDiscount
        ? OrderDiscountType.CustomDiscount
        : OrderDiscountType.PredefinedDiscount,
    discountID: discount.isCustomDiscount ? undefined : discount.discountID,
    discountValue: discount.value,
    isPercentage: discount.type === DiscountType.Percentage,
    discountRedemptionID: discount.discountRedemptionId,
})
const getOrderPayload = (
    serviceChargeRate: Decimal,
    sellState: SellState,
    pin: string | null | undefined,
    deps: ISaleDependencies,
    sendLines: OrderItemViewModel[],
) => {
    const calcResult = sellState.taxResult
    const maxSortingIndex =
        maxBy(calcResult.taxLines, (a) => a.sortingIndex)?.sortingIndex ?? 0
    const taxLinesMap = keyBy(calcResult.taxLines, 'orderItemId')
    const order = sellState.editingOrder
    const entrieOrderVoidComp = sellState.entireOrderVoidComp
    const selectOnlineStoreId = sellState.selectOnlineStoreId

    const orderCustomerID = sellState.orderCustomerID

    const entireDiscounts = sellState.entireOrderDiscounts

    const entireDiscountsMap = entireDiscounts.map((d) =>
        mapEntireDiscountToOrderDiscount(d),
    )

    const orderLineStatus =
        sellState.orderProcessingMode === OrderProcessingMode.FINISH
            ? OrderLineStatus.Completed
            : OrderLineStatus.New

    const orderPayload: OrderContract = {
        ...order,

        // * Fix Guest Count to 1
        guestCount: 1,
        systemOrderChannelID: OrderChannel.QuickService,
        orderFulfillmentTypeID: sellState.currentFulfilmentType,
        subtotal: calcResult.subtotal,
        totalTaxAmount: calcResult.totalTax,
        totalDueAmount: calcResult.totalDue,
        note: sellState.orderNote,
        voidComp: entrieOrderVoidComp,
        customerID: orderCustomerID,
        // * : Attach discounts for Order-Level
        discounts: entireDiscountsMap,

        pinCode: pin,
        // * : Insert Service Charge Rate here
        serviceChargeRate: serviceChargeRate.toNumber(),

        // * : this flag should be removed in real production
        // set `true` if need to skip backend validation
        skipValidation: false,
        onlineStoreID: selectOnlineStoreId,

        orderLines: sellState.orderItems.map((orderItem, index) =>
            toQuickServiceOrderLineContract(
                orderItem,
                orderLineStatus,
                deps.itemsMap,
                sendLines.some((a) => a.id === orderItem.id),
                taxLinesMap,
                maxSortingIndex + index + 1,
            ),
        ),

        // pickup and delivery order
        isAsap: sellState.isAsap,
        pickUpDateTime: sellState.pickupDateTime,

        // delivery order
        deliveryAddress: sellState.deliveryAddress,
    }
    return orderPayload
}
const buildOfflineOrderPayload = (
    sellState: SellState,
    orderPayload: OrderContract,
    customers: CustomerLookupContract[],
    deps: ISaleDependencies,
): OfflineOrderContract => {
    const customer = customers.find(
        (c) => c.customerID === sellState.orderCustomerID,
    )
    const offlineOrderPayload: OfflineOrderContract = {
        ...orderPayload,
        customerName: customer?.givenName ?? 'Guest',
        orderLines:
            orderPayload.orderLines?.map((line) => {
                const lineItemName =
                    getItemName(deps.itemsMap, line.itemID!) ?? '<unknown item>'
                return {
                    ...line,
                    itemName: lineItemName,
                }
            }) ?? [],
    }

    return offlineOrderPayload
}
export const useQuickServiceSaleContext = () => {
    const context = useContext(QuickServiceSaleContext)
    const { t } = useTranslation()
    const { addItemCounting, ...deps } = useSaleDependenciesContext()
    const {
        isOffline,
        strictTimeAvailabilityOnSales,
        serviceChargeSetting,
        taxes,
        companySetting,
        disableProductSelectionForOutOfStockItems: enableInventoryCheck,
        isCalculateCompAfterDiscount,
    } = usePosContext()
    const { convertToOrderItemViewModels } = useBuildOrderItems()

    const [, getOrder] = useFetchApi(orderService.getOrder)
    const [, reprintLines] = useFetchApi(orderService.reprintOrderLine)
    const [
        ,
        checkOrderStockAvailability,
        checkOrderStockAvailabilityStatus,
    ] = useFetchApi(inventoryService.checkOrderStockAvailability)

    const { currencyLanguage, currencyCode } = useFormat()
    const dispatch = useDispatch()
    const { clearPermission, pin } = usePermission()
    const {
        orderChannelID,
        entireOrderVoidComp,
        currentFulfilmentType,
        editingOrder: order,
        orderNote,
        dispatch: saleDispatch,
        orderItems,
        excludeAutoDiscountIds,
        selectedMenuSectionId,
        selectedMenu,
        waitingSentToKdsOrderLineIds,
    } = context

    const {
        redirectToOrder,
        redirectToTable,
        orderId: routeOrderId,
    } = useOrderQuery()

    const sellState = useMemo(
        () => ({ ...context, language: currencyLanguage, currencyCode }),
        [context, currencyCode, currencyLanguage],
    )

    const isSelectedMenuSectionUnavailable = useMemo(
        () =>
            !validateAvailability(
                selectedMenu?.menuSectionAvailabilitiesMap?.[
                    selectedMenuSectionId
                ],
            ),
        [selectedMenu, selectedMenuSectionId],
    )

    const setTriggerSendToOrder = useCallback(
        (send: boolean) => {
            saleDispatch({ type: 'SET_SEND_TO_CFS', payload: send })
        },
        [saleDispatch],
    )
    const finalizeCalculation = useCallback(
        (injectedDiscounts?: DiscountContract[]) => {
            saleDispatch({
                type: 'SELL_FINALIZE_CALCULATION',
                payload: {
                    companySetting,
                    serviceChargeSetting,
                    taxes,
                    discounts: injectedDiscounts ?? deps.discounts,
                    isCalculateCompAfterDiscount,
                    modifierOptionMap: deps.modifierOptionsMap,
                    itemsMap: deps.itemsMap,
                },
            })
            saleDispatch({ type: 'REMOVE_DISCOUNT_INAPPLICABLE' })
            setTriggerSendToOrder(true)
        },
        [
            companySetting,
            deps.discounts,
            deps.itemsMap,
            deps.modifierOptionsMap,
            isCalculateCompAfterDiscount,
            saleDispatch,
            serviceChargeSetting,
            setTriggerSendToOrder,
            taxes,
        ],
    )
    const replaceOrder = useCallback(
        (order: OrderLookupContract) => {
            const loadedOrderItems = convertToOrderItemViewModels(
                order?.orderLines || [],
            )

            saleDispatch({
                type: 'SELL_LOAD_ORDER_SUCCESS',
                payload: {
                    order: order!,
                    orderItems: loadedOrderItems.orderItems,
                    removedData: loadedOrderItems.removedData,
                    discounts: deps.discounts,
                    language: currencyLanguage,
                    currencyCode,
                },
            })
            finalizeCalculation()
        },
        [
            convertToOrderItemViewModels,
            saleDispatch,
            deps.discounts,
            currencyLanguage,
            currencyCode,
            finalizeCalculation,
        ],
    )
    const onLoadOrder = useCallback(
        async (orderId: string, orderCache?: OrderDetailContract) => {
            saleDispatch({ type: 'SELL_LOAD_ORDER_BEGIN' })
            try {
                const order = orderCache
                    ? orderCache
                    : isOffline
                    ? await getOfflineOrder(deps, orderId)
                    : await getOrder(orderId)
                if (order?.tableGroupID || order?.tableID) {
                    redirectToTable({
                        tableID: order.tableID!,
                        tableGroupID: order.tableGroupID!,
                    })
                    return
                }

                replaceOrder(order!)
                if (routeOrderId !== order?.orderID)
                    redirectToOrder(order?.orderID)
            } catch {
                saleDispatch({ type: 'SELL_LOAD_ORDER_FAILED' })
            }
        },
        [
            saleDispatch,
            isOffline,
            deps,
            getOrder,
            replaceOrder,
            routeOrderId,
            redirectToOrder,
            redirectToTable,
        ],
    )

    const addBogoItems = useCallback(
        (updateItems: OrderItemViewModel[]) => {
            //filter update item out
            let itemsToBeUpdated = orderItems.filter((o) => {
                return !updateItems.some((u) => u.id === o.id)
            })
            sortBy(updateItems, (i) => i.discounts?.length ?? 0).forEach(
                (i) => {
                    //add update item to new orders
                    const { newOrderItems } = addItemCounting(
                        itemsToBeUpdated,
                        i,
                        waitingSentToKdsOrderLineIds,
                    )
                    itemsToBeUpdated = newOrderItems
                },
            )
            saleDispatch({
                type: 'SELL_ADD_BOGO_ITEM',
                payload: {
                    items: [...itemsToBeUpdated.filter((a) => a.amount > 0)],
                },
            })
            finalizeCalculation()
        },
        [
            addItemCounting,
            orderItems,
            waitingSentToKdsOrderLineIds,
            finalizeCalculation,
            saleDispatch,
        ],
    )

    const resetServiceChargeRate = useCallback(() => {
        saleDispatch({
            type: 'SELL_RESET_ORDER_SERVICE_CHARGE',
        })
        finalizeCalculation()
    }, [finalizeCalculation, saleDispatch])

    const clearOrder = useCallback(() => {
        saleDispatch({ type: 'SELL_CLEAR_ORDER' })
        clearPermission()
        resetServiceChargeRate()
        redirectToOrder()
    }, [saleDispatch, clearPermission, resetServiceChargeRate, redirectToOrder])

    const onReprint = useCallback(
        (selectedItems: OrderItemViewModel[]) => {
            return reprintLines(
                selectedItems
                    .filter((a) => Guid.isValid(a.id))
                    .map((a) => a.id),
            )
        },
        [reprintLines],
    )

    const loadFulfilment = useCallback(async () => {
        saleDispatch({ type: 'SELL_LOAD_FULFILMENT_BEGIN' })
        try {
            const result = (
                await orderFulfillmentService.getLocationOrderFulfillment()
            ).data
            saleDispatch({
                type: 'SELL_LOAD_FULFILMENT_SUCCESS',
                payload: result,
            })
        } catch (err) {
            dispatch(showErrorMessage(err))
            saleDispatch({ type: 'SELL_LOAD_FULFILMENT_FAILED' })
        }
    }, [dispatch, saleDispatch])

    const onVoidOrder = useCallback(
        async (serviceChargeRate: Decimal, newReason?: VoidCompReasonModel) => {
            saleDispatch({
                type: 'SELL_PROCESS_ORDER_BEGIN',
            })
            const { QuickService } = OrderChannel
            try {
                const orderPayload: OrderContract = {
                    ...order,
                    systemOrderChannelID: QuickService,
                    orderFulfillmentTypeID: currentFulfilmentType,
                    subtotal: 0,
                    totalTaxAmount: 0,
                    totalDueAmount: 0,
                    note: orderNote,
                    pinCode: pin,
                    // * : Insert Service Charge Rate here
                    serviceChargeRate: serviceChargeRate.toNumber(),
                    voidComp:
                        newReason ??
                        (entireOrderVoidComp as VoidCompReasonModel),
                }

                if (isOffline) await voidOfflineOrder(orderPayload)
                else {
                    const voidRequest = toVoidOrderRequest(orderPayload, pin)
                    try {
                        await orderService.voidOrder(voidRequest)
                    } catch (err: any) {
                        const errorCode = getCommonError(err)
                        if (errorCode === CommonError.OrderNotFound) {
                            // handle case try to void offline order with Cloud API (without previously saved)
                            // try load saved order from cache
                            await onOrderNotFound(
                                orderPayload.orderID!,
                                async () => {
                                    return orderService.voidOrder(voidRequest)
                                },
                            )
                        } else throw err
                    }
                }
                saleDispatch({ type: 'SELL_VOID_ORDER_SUCCESS' })
            } catch (error) {
                dispatch(showErrorMessage(error))
                saleDispatch({ type: 'SELL_VOID_ORDER_FAILED' })
            }
        },
        [
            currentFulfilmentType,
            dispatch,
            entireOrderVoidComp,
            isOffline,
            order,
            orderNote,
            pin,
            saleDispatch,
        ],
    )

    const onSelectMenuSection = useCallback(
        (id: string) => {
            saleDispatch({ type: 'SELL_SELECT_MENUSECTION', payload: id })
        },
        [saleDispatch],
    )
    const inputSearchItem = useCallback(
        (search: string) => {
            saleDispatch({
                type: 'SELL_SEARCH_ITEM',
                payload: search,
            })
        },
        [saleDispatch],
    )
    const onSelectMenu = useCallback(
        (menu: MenuViewModel) => {
            saleDispatch({
                type: 'SELL_SELECT_MENU',
                payload: menu,
            })
        },
        [saleDispatch],
    )
    const onSelectCategory = useCallback(
        (categoryId: string) =>
            saleDispatch({
                type: 'SELL_SELECT_CATEGORY',
                payload: categoryId,
            }),
        [saleDispatch],
    )

    const addSellItems = useCallback(
        (newItems: OrderItemViewModel[], injectedDeps?: ISaleDependencies) => {
            const errors = new OrderItemViewError(
                strictTimeAvailabilityOnSales,
                deps.menus,
                deps.comboChoicesMap,
                deps.unavailableProducts,
                enableInventoryCheck ?? false,
                deps.modifierOptionsMap,
                deps.priceListData,
                deps.variantGroupsMap,
                deps.modifierGroupsMap,
                deps.itemsMap,
                deps.entityModifiersMap,
            )
            newItems.forEach((l) => {
                errors.validateOrderItemsView(l)
                l.error = errors.errors
            })
            const discounts = injectedDeps?.discounts ?? deps.discounts

            const autoItemDiscounts = discounts.filter(
                (f) =>
                    f.isAutomaticDiscount &&
                    f.level === DiscountLevel.Item &&
                    f.type &&
                    SIMPLE_DISCOUNT_TYPE.includes(f.type),
            )
            let orderItemsAppended: OrderItemViewModel[] = orderItems
            let latestOrderItem: OrderItemViewModel = newItems[0]

            newItems.forEach((l) => {
                const { newOrderItems, newAddedItem } = addItemCounting(
                    orderItemsAppended,
                    l,
                    waitingSentToKdsOrderLineIds,
                )
                orderItemsAppended = [...newOrderItems]
                latestOrderItem = newAddedItem
            })

            const itemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: autoItemDiscounts,
                checkingFulfillmentType: currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannel: orderChannelID,
            })(orderItemsAppended)
            saleDispatch({
                type: 'SELL_ADD_ITEM',
                payload: {
                    items: itemsWithDiscounts,
                    last: itemsWithDiscounts.find(
                        (i) => i.id === latestOrderItem.id,
                    ),
                },
            })
            finalizeCalculation(discounts)
        },
        [
            strictTimeAvailabilityOnSales,
            deps.menus,
            deps.comboChoicesMap,
            deps.unavailableProducts,
            deps.modifierOptionsMap,
            deps.priceListData,
            deps.variantGroupsMap,
            deps.modifierGroupsMap,
            deps.itemsMap,
            deps.entityModifiersMap,
            deps.discounts,
            enableInventoryCheck,
            addItemCounting,
            orderItems,
            waitingSentToKdsOrderLineIds,
            currentFulfilmentType,
            excludeAutoDiscountIds,
            orderChannelID,
            saleDispatch,
            finalizeCalculation,
        ],
    )

    const modifySellItem = useCallback(
        (
            item: OrderItemViewModel,
            newItem: OrderItemViewModel,
            injectedDeps?: ISaleDependencies,
        ) => {
            const discounts = injectedDeps?.discounts ?? deps.discounts
            const menus = injectedDeps?.menus ?? deps.menus
            const priceListData =
                injectedDeps?.priceListData ?? deps.priceListData
            const itemAutoDiscounts = getItemLevelAutomaticDiscounts(discounts)

            let newOrderItems = [...orderItems]

            const existOrderItem = newOrderItems
                .filter((i) => i.id !== newItem.id)
                .find(
                    (i) =>
                        isSameOrderItem(i, newItem) &&
                        isUseSamePriceList(menus, priceListData, i, newItem),
                )

            if (existOrderItem) {
                existOrderItem.amount += newItem.amount
                newOrderItems = newOrderItems.filter((i) => !isEqual(i, item))
            } else {
                newOrderItems = newOrderItems.map((e) => {
                    if (isEqual(e, item)) {
                        return newItem
                    }
                    return e
                })
            }
            const itemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: itemAutoDiscounts,
                checkingFulfillmentType: currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannel: orderChannelID,
            })(newOrderItems)
            saleDispatch({
                type: 'SELL_ADD_ITEM',
                payload: {
                    items: itemsWithDiscounts,
                    last: existOrderItem ?? newItem,
                },
            })
            finalizeCalculation(discounts)
        },
        [
            currentFulfilmentType,
            deps.discounts,
            deps.menus,
            deps.priceListData,
            excludeAutoDiscountIds,
            finalizeCalculation,
            orderChannelID,
            orderItems,
            saleDispatch,
        ],
    )

    const modifyItem = useCallback(
        (
            item: OrderItemViewModel,
            newItem: OrderItemViewModel,
            isMakeAnother: boolean = false,
        ) => {
            newItem.isSaved = false
            const convertedItem = isMakeAnother
                ? changeBaseItemIDToVariantID(newItem)
                : newItem
            modifySellItem(item, convertedItem, deps)
        },
        [deps, modifySellItem],
    )

    const removeItem = useCallback(
        (item: ISelectItemInCart) => {
            saleDispatch({
                type: 'SELL_REMOVE_ITEM',
                payload: item,
            })
            finalizeCalculation()
        },
        [saleDispatch, finalizeCalculation],
    )
    const removeVoidCompItem = useCallback(
        (item: OrderItemViewModel) => {
            saleDispatch({ type: 'SELL_REMOVE_VOIDCOMP_ITEM', payload: [item] })
            finalizeCalculation()
        },
        [saleDispatch, finalizeCalculation],
    )
    const removeVoidOrder = useCallback(() => {
        saleDispatch({ type: 'SELL_REMOVE_VOID_ORDER' })
        finalizeCalculation()
    }, [saleDispatch, finalizeCalculation])
    const addNote = useCallback(
        (note: string) =>
            saleDispatch({ type: 'SELL_ADD_NOTE', payload: note }),
        [saleDispatch],
    )
    const addPickupInfo = useCallback(
        (pickupInfo: IPickupSettingForm) =>
            saleDispatch({
                type: 'SELL_UPDATE_PICKUP_INFO',
                payload: pickupInfo,
            }),
        [saleDispatch],
    )
    const resetPickupInfo = useCallback(
        () => saleDispatch({ type: 'SELL_RESET_PICKUP_INFO' }),
        [saleDispatch],
    )
    const addDeliveryInfo = useCallback(
        (form: IDeliverySettingForm) =>
            saleDispatch({
                type: 'SELL_UPDATE_DELIVERY_INFO',
                payload: form,
            }),
        [saleDispatch],
    )
    const resetDeliveryInfo = useCallback(
        () => saleDispatch({ type: 'SELL_RESET_DELIVERY_INFO' }),
        [saleDispatch],
    )
    const setServiceChargeRate = useCallback(
        (serviceChargeRate: number) => {
            saleDispatch({
                type: 'SELL_SET_ORDER_SERVICE_CHARGE',
                payload: serviceChargeRate,
            })
            finalizeCalculation()
        },
        [finalizeCalculation, saleDispatch],
    )
    const removeServiceChargeRate = useCallback(() => {
        saleDispatch({
            type: 'SELL_REMOVE_ORDER_SERVICE_CHARGE',
        })
        finalizeCalculation()
    }, [finalizeCalculation, saleDispatch])

    const setDefaultServiceChargeRate = useCallback(
        (serviceChargeRate: number) => {
            saleDispatch({
                type: 'SET_DEFAULT_SERVICE_CHARGE_RATE',
                payload: serviceChargeRate,
            })
            finalizeCalculation()
        },
        [finalizeCalculation, saleDispatch],
    )
    const recalculateOrder = useCallback(
        (orderView: OrderItemViewModel) => {
            const newOrderItems = cloneDeep(context.orderItems).map((oItem) => {
                if (oItem.id === orderView.id) {
                    const recalculateOrder = new RecalculateOrder(
                        oItem,
                        orderView.menuId!,
                        deps.priceListData,
                        deps.comboChoicesMap,
                        deps.entityModifiersMap,
                    )
                    recalculateOrder.process()
                    return recalculateOrder.getOrderItemView()
                }
                return {
                    ...oItem,
                }
            })

            saleDispatch({
                type: 'RE_CALCULATE_LINE_TOTAL',
                payload: newOrderItems,
            })

            finalizeCalculation()
        },
        [
            context.orderItems,
            saleDispatch,
            finalizeCalculation,
            deps.priceListData,
            deps.comboChoicesMap,
            deps.entityModifiersMap,
        ],
    )

    const onCheckOrderStockAvailability = useCallback(
        async (orderID: string) => {
            if (isOffline) return //not validate in offline mode
            try {
                await checkOrderStockAvailability(orderID)
            } catch (err: any) {
                let { message, arg } = errorMessages(err)

                saleDispatch({
                    type: 'SELL_PROCESS_ORDER_FAILED',
                    payload: { message, additionValue: arg },
                })

                throw err
            }
        },
        [checkOrderStockAvailability, isOffline, saleDispatch],
    )

    const processOrder = useCallback(
        async (
            serviceChargeRate: Decimal,
            customers: CustomerLookupContract[],
            reloadOrderAfterSave: boolean = true,
        ) => {
            saleDispatch({
                type: 'SELL_PROCESS_ORDER_BEGIN',
            })
            const orderPayload: OrderContract = getOrderPayload(
                serviceChargeRate,
                sellState,
                pin,
                deps,
                [],
            )

            let orderID

            let res = {} as AxiosResponse<ProcessSaleContract>

            if (isOffline) {
                res = await processOfflineOrder(
                    buildOfflineOrderPayload(
                        sellState,
                        orderPayload,
                        customers,
                        deps,
                    ),
                )
            } else {
                try {
                    res = await orderService.processOrder(orderPayload)
                } catch (error) {
                    let { message } = errorMessages(error)

                    saleDispatch({
                        type: 'SELL_PROCESS_ORDER_FAILED',
                        payload: { message },
                    })
                    console.error('[SELL_PROCESS_ORDER_FAILED]', {
                        orderPayload,
                        result: sellState.taxResult,
                    })
                    throw new Error(message)
                }
                // These below lines are meant to support edge case, success saved order online but failed to do payment in online
                // assign order API generated from Cloud API
                orderPayload.orderID = res.data.order.orderID

                // save orderPayload in offline DB, to later support offline payment (rare case)
                pendingOrderDbService.saveOrder(
                    buildOfflineOrderPayload(
                        sellState,
                        orderPayload,
                        customers,
                        deps,
                    ),
                )
            }

            saleDispatch({
                type: 'SELL_PROCESS_ORDER_SUCCESS',
                payload: {
                    order: res.data.order,
                    sentOrderLineIds: [],
                    isOffline: isOffline ?? false,
                },
                message: '',
            })

            orderID = res.data.order.orderID!

            redirectToOrder(orderID)
            if (
                sellState.orderProcessingMode === OrderProcessingMode.PREVIEW &&
                isOffline
            )
                saleDispatch({ type: 'SELL_PREVIEW_BILL_OFFLINE' })

            if (reloadOrderAfterSave) {
                onLoadOrder(orderID)
            }
            return orderID
        },
        [
            deps,
            isOffline,
            onLoadOrder,
            pin,
            redirectToOrder,
            saleDispatch,
            sellState,
        ],
    )
    const onSelectedItemCart = useCallback(
        (selectItem: ISelectItemInCart | undefined) => {
            saleDispatch({
                type: 'SELECT_ORDER_CART_ITEM',
                payload: selectItem,
            })
        },
        [saleDispatch],
    )

    const saveAndSendOrder = useCallback(
        async (
            serviceChargeRate: Decimal,
            selectedLines?: OrderItemViewModel[],
        ) => {
            saleDispatch({
                type: 'SELL_PROCESS_ORDER_BEGIN',
            })
            const orderPayload: OrderContract = getOrderPayload(
                serviceChargeRate,
                sellState,
                sellState.pin,
                deps,
                selectedLines ?? [],
            )

            let updatedOrder: OrderContract | null = null
            let sentOrderLineIds: string[] | null | undefined = undefined

            try {
                const res = await orderService.processAndSendOrder(orderPayload)
                updatedOrder = res.data.order
                sentOrderLineIds = res.data.sentOrderLineIds
            } catch (error) {
                let { message, arg } = errorMessages(error)
                saleDispatch({
                    type: 'SELL_PROCESS_ORDER_FAILED',
                    payload: { message, additionValue: arg },
                })
                throw new Error(message, arg)
            }
            if (!updatedOrder?.isSent) {
                toast.error(
                    t('sell.message.send_failed', {
                        count: selectedLines?.length ?? 0,
                    }),
                )
            } else
                toast.success(
                    t('sell.message.send_successful', {
                        count: selectedLines?.length ?? 0,
                    }),
                )
            saleDispatch({
                type: 'SELL_PROCESS_ORDER_SUCCESS',
                payload: {
                    order: updatedOrder!,
                    sentOrderLineIds: sentOrderLineIds ?? [],
                    isOffline: false,
                },
                message: '',
            })
            onLoadOrder(updatedOrder?.orderID!)
            onSelectedItemCart(undefined)
        },
        [deps, onLoadOrder, onSelectedItemCart, saleDispatch, sellState, t],
    )

    const idleSellProcess = useCallback(
        () => saleDispatch({ type: 'SELL_PROCESS_SET_IDLE' }),
        [saleDispatch],
    )

    const setOrderProcessingMode = useCallback(
        (mode: OrderProcessingMode) => {
            saleDispatch({
                type: 'SELL_SET_ORDER_PROCESSING_MODE',
                payload: mode,
            })
        },
        [saleDispatch],
    )

    const makeAnother = useCallback(
        (item: OrderItemViewModel) => {
            const convertedNewItem = changeBaseItemIDToVariantID(item)

            /**
             * for now apply ONLY FixedAmount type and Percentage type
             */
            const itemLevelAutomaticDiscounts = getItemLevelAutomaticDiscounts(
                deps.discounts,
            )
            const { newOrderItems, newAddedItem } = addItemCounting(
                orderItems,
                convertedNewItem,
                waitingSentToKdsOrderLineIds,
            )

            const itemsWithDiscounts = autoDiscountByItemsMapping({
                automaticDiscounts: itemLevelAutomaticDiscounts,
                checkingFulfillmentType: currentFulfilmentType,
                excludeAutoDiscountIds,
                orderChannel: orderChannelID,
            })(newOrderItems)

            saleDispatch({
                type: 'SELL_MAKE_ANOTHER',
                payload: {
                    newOrderItems: itemsWithDiscounts,
                    newMakedAnotherItem: itemsWithDiscounts.find(
                        (i) => i.id === newAddedItem.id,
                    ),
                },
            })

            finalizeCalculation()
        },
        [
            deps.discounts,
            addItemCounting,
            orderItems,
            waitingSentToKdsOrderLineIds,
            currentFulfilmentType,
            excludeAutoDiscountIds,
            orderChannelID,
            saleDispatch,
            finalizeCalculation,
        ],
    )

    const setCurrentFulfilmentType = useCallback(
        (type: OrderFulfilmentType, needToResetServiceCharge?: boolean) => {
            needToResetServiceCharge = needToResetServiceCharge ?? true
            saleDispatch({
                type: 'SELL_SET_CURRENT_FULFILMENT_TYPE',
                payload: type,
            })
            if (needToResetServiceCharge) {
                saleDispatch({
                    type: 'SELL_RESET_ORDER_SERVICE_CHARGE',
                })
            }
        },
        [saleDispatch],
    )

    const closeAlertDiscountInApplicable = useCallback(() => {
        saleDispatch({
            type: 'ALERT_DISCOUNT_INAPPLICABLE',
            payload: false,
        })
    }, [saleDispatch])

    const onOrderItemUnavailable = useCallback(
        (
            lineId: string,
            isUnavailable: boolean,
            errors?: ErrorOrderItemViewType,
        ) => {
            saleDispatch({
                type: 'SELL_SET_ITEM_ORDER_ERRORS',
                payload: {
                    lineId,
                    isUnavailable,
                    errors,
                },
            })
        },
        [saleDispatch],
    )

    const updateOrderAndOrderLinesStatus = useCallback(
        async (orderID: string) => {
            const res = await orderService.getOrder(orderID)
            saleDispatch({
                type: 'UPDATE_ORDER_AND_ORDER_LINES_STATUS',
                payload: res.data,
            })
            finalizeCalculation()
        },
        [finalizeCalculation, saleDispatch],
    )
    const onSelectOnlineStore = useCallback(
        (onlineStoreID: string) => {
            const selectOnlineStore = deps.onlineStores.find(
                (a) => a.onlineStoreID === onlineStoreID,
            )
            const selectedMenu = deps.menus.find(
                (m) =>
                    m.menuId ===
                    //online store can has only 1 menu
                    selectOnlineStore?.onlineStoreMenus?.[0]?.menuID,
            )
            saleDispatch({
                type: 'SELL_SELECT_ONLINE_STORE',
                payload: { selectedMenu, onlineStoreID },
            })
        },
        [deps.menus, deps.onlineStores, saleDispatch],
    )
    const onSelectBrand = useCallback(
        (brandID: string) => {
            saleDispatch({ type: 'SELL_SELECT_BRAND_ID', payload: brandID })
        },
        [saleDispatch],
    )

    const setOpenCustomerDialog = useCallback(
        (open: boolean) => {
            saleDispatch({
                type: 'SET_CUSTOMER_DIALOG_OPEN_STATUS',
                payload: open,
            })
        },
        [saleDispatch],
    )
    const setOpenGiftCardDialog = useCallback(
        (isOpen: boolean) => {
            saleDispatch({
                type: 'SET_GIFT_CARD_DIALOG_OPEN_STATUS',
                payload: isOpen,
            })
        },
        [saleDispatch],
    )

    const setOpenReceiptLookupDialog = useCallback(
        (isOpen: boolean) => {
            saleDispatch({
                type: 'SET_RECEIPT_LOOKUP_DIALOG_OPEN_STATUS',
                payload: isOpen,
            })
        },
        [saleDispatch],
    )

    const setOpenVoidDialog = useCallback(
        (isOpen: boolean) => {
            saleDispatch({
                type: 'SET_VOID_DIALOG_OPEN_STATUS',
                payload: isOpen,
            })
        },
        [saleDispatch],
    )

    const applySelectVoidCompOrder = useCallback(
        (
            voidComp: VoidCompReasonModel | undefined,
            items: OrderItemViewModel[],
            pin?: string,
        ) => {
            saleDispatch({
                type: 'SELL_ORDER_VOIDCOMP',
                payload: {
                    items,
                    voidComp,
                    pin,
                },
            })
            finalizeCalculation()
        },
        [saleDispatch, finalizeCalculation],
    )
    const updateOrderPaymentDetail = useCallback(
        (sale: SaleSlimModel) => {
            const payload = {
                orderPaymentStatus: sale.order!.orderPaymentStatus,
                orderStatus: sale.order?.status,
                payments: sale.payments || [],
            }
            saleDispatch({
                type: 'SELL_UPDATE_ORDER_PAYMENTS',
                payload,
            })
        },
        [saleDispatch],
    )

    const applySelectDiscountItems = useCallback(
        (
            items: OrderItemViewModel[],
            entireDiscounts: DiscountContract[],
            resetEntire: boolean = false,
            pin?: string,
        ) => {
            saleDispatch({
                type: 'APPLY_SELECT_DISCOUNT_ITEMS',
                payload: { items, entireDiscounts, resetEntire, pin },
            })
            finalizeCalculation()
        },
        [saleDispatch, finalizeCalculation],
    )

    const assignCustomerToOrder = useCallback(
        (customerID: string) => {
            saleDispatch({
                type: 'SELL_ASSIGN_CUSTOMER_TO_ORDER',
                payload: customerID,
            })
        },
        [saleDispatch],
    )
    const assignCustomerAddress = useCallback(
        (addressID: string) => {
            saleDispatch({
                type: 'SELL_SELECT_CUSTOMER_ADDRESS',
                payload: addressID,
            })
        },
        [saleDispatch],
    )
    /** unable to check price changed anymore because this object transform to be view not real orderline */
    const validateItemViewError = useCallback(
        (newUnavailableProduct: UnavailableItemsContract) => {
            saleDispatch({
                type: 'CHECK_ITEM_VIEW_ERROR',
                payload: {
                    errorCheck: new OrderItemViewError(
                        strictTimeAvailabilityOnSales,
                        deps.menus,
                        deps.comboChoicesMap,
                        newUnavailableProduct,
                        enableInventoryCheck ?? false,
                        deps.modifierOptionsMap,
                        deps.priceListData,
                        deps.variantGroupsMap,
                        deps.modifierGroupsMap,
                        deps.itemsMap,
                        deps.entityModifiersMap,
                    ),
                },
            })
        },
        [
            saleDispatch,
            strictTimeAvailabilityOnSales,
            deps.menus,
            deps.comboChoicesMap,
            deps.modifierOptionsMap,
            deps.priceListData,
            deps.variantGroupsMap,
            deps.modifierGroupsMap,
            deps.itemsMap,
            deps.entityModifiersMap,
            enableInventoryCheck,
        ],
    )

    const addItemDispatch = useCallback(
        (item: OrderItemViewModel) => {
            const convertedItem = changeBaseItemIDToVariantID(item)
            addSellItems([convertedItem], deps)
        },
        [addSellItems, deps],
    )
    const addItemsDispatch = useCallback(
        (item: OrderItemViewModel[]) => {
            const convertedItems = item.map((i) =>
                changeBaseItemIDToVariantID(i),
            )
            addSellItems(convertedItems, deps)
        },
        [addSellItems, deps],
    )
    const updateTransactionProcessStatus = useCallback(
        (isProcessing: boolean) => {
            saleDispatch({
                type: 'UPDATE_TRANSACTION_PROCESS_STATUS',
                payload: isProcessing,
            })
        },
        [saleDispatch],
    )
    const disableEditOrder = useMemo(() => {
        return (
            context.hasAwaitingProcessTransaction ||
            isOrderHasPayment(context.editingOrder)
        )
    }, [context.editingOrder, context.hasAwaitingProcessTransaction])

    const updateOrderLinesKdsStatus = useCallback(
        (orderLines: OrderLineNotificationContract[]) => {
            saleDispatch({
                type: 'UPDATE_ITEM_KDS_STATUS',
                payload: {
                    orderLines,
                },
            })
        },
        [saleDispatch],
    )

    const updateOrderStatus = useCallback(
        (order: OrderNotificationContract) => {
            saleDispatch({
                type: 'UPDATE_ORDER_STATUS',
                payload: order,
            })
        },
        [saleDispatch],
    )

    return {
        ...context,
        onLoadOrder,
        onSelectMenu,
        onSelectCategory,
        processOrder,
        inputSearchItem,
        addItem: addItemDispatch,
        addItems: addItemsDispatch,
        modifyItem,
        removeItem,
        removeVoidOrder,
        removeVoidCompItem,
        addNote,
        addPickupInfo,
        resetPickupInfo,
        addDeliveryInfo,
        resetDeliveryInfo,
        idleSellProcess,
        clearOrder,
        setOrderProcessingMode,
        loadFulfilment,
        voidOrder: onVoidOrder,
        makeAnother,
        setCurrentFulfilmentType,
        closeAlertDiscountInApplicable,
        saveAndSendOrder,
        onOrderItemUnavailable,
        updateOrderAndOrderLinesStatus,
        onSelectBrand,
        onSelectedItemCart,
        onSelectOnlineStore,
        setOpenCustomerDialog,
        setOpenGiftCardDialog,
        setOpenReceiptLookupDialog,
        setOpenVoidDialog,
        applySelectVoidCompOrder,
        updateOrderPaymentDetail,
        applySelectDiscountItems,
        addBogoItems,
        onSelectMenuSection,
        assignCustomerAddress,
        assignCustomerToOrder,
        setServiceChargeRate,
        removeServiceChargeRate,
        resetServiceChargeRate,
        setDefaultServiceChargeRate,
        updateTransactionProcessStatus,
        /** recalculate price of order item when the order change other pricelist. */
        recalculateOrder,
        validateItemViewError,
        disableEditOrder,
        /** check order stock availablity before make payment */
        onCheckOrderStockAvailability,
        isCheckingOrderStockAvailability: isLoading(
            checkOrderStockAvailabilityStatus,
        ),
        setTriggerSendToOrder,
        onReprint,
        updateOrderLinesKdsStatus,
        updateOrderStatus,
        isSelectedMenuSectionUnavailable,
    }
}
export async function onOrderNotFound<T>(
    orderID: string,
    callback: () => Promise<AxiosResponse<T>>,
) {
    const pendingTransaction = await pendingOrderDbService.getById(orderID)
    if (!pendingTransaction)
        throw new Error(
            'Process with invalid order data. Please restart void process',
        )

    if (pendingTransaction.orderStatus !== OrderStatus.Saved)
        throw new Error('Invalid order status. Please restart void process')

    // Call ProcessOrder API to create new order in 'Saved' state
    await orderService.processOrder(pendingTransaction.order)

    // Then, call Void Order API
    const res = await callback()

    // remove cache when successfully void
    await pendingOrderDbService.deleteById(orderID)
    return res
}
